// /src/components/SliderWithLabel.js
import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Define common styles using styled-components
const commonStyles = css`
  font-family: Arial, sans-serif;
  font-size: 1.4rem;
  font-weight: 400;
`;

// Define styled components relevant to the SliderWithLabel component
const SliderLabel = styled.label`
  ${commonStyles}
  display: block;
  margin-bottom: 70px; /* Responsive margin */
  margin-top: 30px; /* Responsive margin */
  font-weight: 400;
`;

const SliderContainer = styled.div`
  position: relative;
  margin-top: 30px; /* Responsive margin */
`;

const ClickableLegend = styled.span`
  ${commonStyles}
  font-size: 1.2rem;
  font-weight: 400;
  margin-left: 7px; /* Responsive margin */
  text-align: ${(props) => props.textAlign || 'center'};
  width: ${(props) => props.width || '15vw'}; /* Responsive width */
  display: block;
  position: absolute;
  top: -45px; /* Adjusted to position legends above ticks */
  left: ${(props) => props.left || '0%'};
  transform: translateX(-50%);
  cursor: pointer;
  z-index: 1;
  pointer-events: all; /* Ensure it captures click events */
`;

const ClickableValue = styled.span`
  ${commonStyles}
  font-size: 1.4rem;
  font-weight: 700;
  display: block;
  text-align: center;
  position: absolute;
  bottom: -30px; /* Adjusted to position values below ticks */
  left: ${(props) => props.left || '0%'};
  transform: translateX(-50%);
  cursor: pointer;
  z-index: 1;
  pointer-events: all; /* Ensure it captures click events */
`;

const TickContainer = styled.div`
  position: absolute;
  left: ${(props) => props.left || '0%'};
  transform: translateX(-50%);
  z-index: 1;
  pointer-events: none; /* Make the tick itself non-clickable */
`;

const TickLine = styled.div`
  height: 10px; /* Height of the line segment */
  width: 1px; /* Thickness of the line segment */
  background-color: ${(props) => props.color || 'black'};
  position: absolute;
  top: -8px; /* Position of the line segment */
  left: 0;
  transform: translateX(-50%);
`;

const ClickableTickOverlay = styled.div`
  height: 24px; /* Clickable area height */
  width: 24px; /* Clickable area width */
  position: absolute;
  top: -16px; /* Position of the clickable area */
  left: -12px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  pointer-events: all; /* Make the clickable area capture click events */
`;

//background-color: rgba(255, 0, 0, 0.3);

const Slider = styled.input`
  width: 48vw;
  height: ${(props) => props.thickness || '5px'};
  -webkit-appearance: none;
  appearance: none;
  background: ${(props) => props.color || '#ddd'};
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;
  position: relative;
  z-index: 2;

  @media (min-width: 1230px) {
    width: 600px; /* Set to a fixed width */
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 10px; /* Responsive thumb width */
    height: 10px; /* Responsive thumb height */
    background: ${(props) => props.thumbColor || '#4CAF50'};
    border-radius: 50%;
    cursor: pointer;
    margin-top: 0px; /* Adjusted margin */
  }

  &::-moz-range-thumb {
    width: 10px; /* Responsive thumb width */
    height: 10px; /* Responsive thumb height */
    background: ${(props) => props.thumbColor || '#4CAF50'};
    border-radius: 50%;
    cursor: pointer;
    margin-top: 0px; /* Adjusted margin */
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px; /* Responsive margin */
`;

// Component definition
const SliderWithLabel = ({ label, min, max, step, thickness, color, thumbColor, value, onChange, width, winwid }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const formatNumberForLocale = (number, locale) => {
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 20,
    }).format(number);
  };

  const numTicks = 4;
  const ticks = Array.from({ length: numTicks + 1 }, (_, i) => max - i * ((max - min) / numTicks));
  const legends = [
    { text: t('Optimal hold'), width: '40px', textAlign: 'left' },
    { text: t('Rough surface'), width: '60px', textAlign: 'center' },
    { text: t('Smooth, dry surface'), width: '100px', textAlign: 'center' },
    { text: t('Wet surface'), width: '60px', textAlign: 'center' },
    { text: t('Oily surface'), width: '60px', textAlign: 'right' }
  ];

  const leftValuesValues = ['2%', '25.7%', '50%', '74.7%', '98%'];

  let leftValuesTick;
  let leftValuesLegend;
  /*
  if (winwid > 1700) {
    leftValuesTick = ['1.2%', '25.6%', '50%', '74.4%', '98.6%'];
    leftValuesLegend = ['1%', '24%', '48%', '72%', '95%'];
  } else if ((winwid <= 1700) && (winwid > 1550)) {
    leftValuesTick = ['1.3%', '25.6%', '50%', '74.3%', '98.6%'];
    leftValuesLegend = ['1%', '24%', '48%', '72%', '95%'];
  } else if ((winwid <= 1550) && (winwid > 1300)) {
    leftValuesTick = ['1.6%', '25.7%', '50%', '74.2%', '98.4%'];
    leftValuesLegend = ['1%', '24%', '48%', '72%', '95%'];
  } else if ((winwid <= 1300) && (winwid > 1100)) {
    leftValuesTick = ['1.8%', '25.9%', '50%', '74.0%', '98.1%'];
    leftValuesLegend = ['1%', '23%', '48%', '70%', '93%'];
  } else if ((winwid <= 1100) && (winwid > 1000)) {
    leftValuesTick = ['2.2%', '26.1%', '50%', '73.8%', '97.8%'];
    leftValuesLegend = ['1%', '23%', '48%', '70%', '93%'];
  } else {
    leftValuesTick = ['2.6%', '26.3%', '50%', '73.8%', '97.6%'];
    leftValuesLegend = ['1%', '21%', '46%', '69%', '93%'];
  }
  */
  if (winwid > 1230) {
    leftValuesTick = ['1.1%', '25.7%', '50%', '74.4%', '98.7%'];
    leftValuesLegend = ['2%', '25%', '49%', '73.3%', '95%'];
  } else if ((winwid <= 1230) && (winwid > 1100)) {
    leftValuesTick = ['1.2%', '25.6%', '50%', '74.4%', '98.8%'];
    leftValuesLegend = ['2.5%', '24.5%', '49%', '73%', '94%'];
  } else if ((winwid <= 1100) && (winwid > 1000)) {
    leftValuesTick = ['1.3%', '25.7%', '50%', '74.2%', '98.6%'];
    leftValuesLegend = ['2.5%', '24%', '49%', '73%', '93%'];
  } else {
    leftValuesTick = ['1.3%', '25.7%', '50%', '74.2%', '98.5%'];
    leftValuesLegend = ['2.5%', '24%', '49%', '72.5%', '92%'];
  }

  const handleLegendClick = (index) => {
    const newValue = ticks[4-index];
    onChange({ target: { value: newValue } });
  };

  const handleValueClick = (index) => {
    const newValue = ticks[4-index];
    onChange({ target: { value: newValue } });
  };

  const handleTickClick = (index) => {
    const newValue = ticks[4-index];
    onChange({ target: { value: newValue } });
  };

  return (
    <FormGroup>
      <SliderLabel>{label}</SliderLabel>
      <SliderContainer>
        <Slider
          type="range"
          min={min}
          max={max}
          step={step}
          thickness={thickness}
          color={color}
          thumbColor={thumbColor} 
          value={value}
          onChange={onChange}
        />
        {ticks.map((tick, index) => (
          <React.Fragment key={index}>
            <TickContainer left={leftValuesTick[index]}>
              <TickLine color={color} />
              <ClickableTickOverlay
                onClick={() => handleTickClick(index)}
              />
            </TickContainer>
            <ClickableLegend 
              left={leftValuesLegend[index]} 
              width={legends[index].width} 
              textAlign={legends[index].textAlign}
              onClick={() => handleLegendClick(index)}
            >
              {legends[index].text}
            </ClickableLegend>
            <ClickableValue 
              left={leftValuesValues[index]}
              onClick={() => handleValueClick(index)}
            >
              {tick === 0 ? formatNumberForLocale(parseFloat(('0.1').replace(',', '.')), locale) : formatNumberForLocale(parseFloat(((tick - 0.01).toFixed(1)).replace(',', '.')), locale)}
            </ClickableValue>
          </React.Fragment>
        ))}
      </SliderContainer>
    </FormGroup>
  );
};

export default SliderWithLabel;
